"use client";

import React from "react";
import { Loader, Send } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { cn } from "@/lib/utils";
function SignUpForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="w-full max-w-md mx-auto p-8 border-2 border-[var(--color-primary)] bg-[var(--color-card)] text-center animate-in fade-in zoom-in duration-500">
        <h3 className="text-xl font-serif text-[var(--color-primary)] mb-4 uppercase tracking-widest">
          Tak for din støtte
        </h3>
        <p className="text-[var(--color-foreground)] mb-6 font-sans">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-none border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-[var(--color-primary-foreground)] uppercase tracking-wider">
          Tilmeld en anden
        </Button>
      </div>;
  }
  const inputClasses = "bg-black/20 border-0 border-b-2 border-[var(--color-primary)] rounded-none px-0 py-6 text-lg focus-visible:ring-0 focus-visible:border-[var(--color-primary)] focus-visible:border-b-[3px] placeholder:text-muted-foreground/50 transition-all duration-300 hover:bg-black/30";
  const labelClasses = "text-xs uppercase tracking-[0.2em] text-[var(--color-muted-foreground)] font-bold ml-1";
  return <Form onSubmit={handleSubmit} className="w-full max-w-2xl mx-auto space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {}
        <FormField control={control} name="full_name" rules={{
        required: "Navn er påkrævet"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Fulde Navn</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="text-red-500 text-xs uppercase tracking-wide" />
            </FormItem>} />

        {}
        <FormField control={control} name="email_address" rules={{
        required: "Email er påkrævet",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ugyldig email adresse"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Email Adresse</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="text-red-500 text-xs uppercase tracking-wide" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {}
        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Telefon (Valgfri)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+45 12 34 56 78" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="text-red-500 text-xs uppercase tracking-wide" />
            </FormItem>} />

        {}
        <FormField control={control} name="support_reason" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Hvorfor støtter du?</FormLabel>
              <FormControl>
                <textarea {...field} placeholder="Del din motivation..." className={cn("flex w-full min-h-[60px] resize-y bg-transparent text-base shadow-xs outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm", inputClasses, "h-14 pt-4")} />
              </FormControl>
              <FormMessage className="text-red-500 text-xs uppercase tracking-wide" />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-red-900/20 border border-red-900/50 text-red-200 text-sm text-center">
          {submitError}
        </div>}

      <div className="pt-4 flex justify-center">
        <Button type="submit" disabled={isSubmitting} className="h-14 px-12 bg-[var(--color-primary)] text-[var(--color-primary-foreground)] hover:bg-[var(--color-primary)]/90 rounded-none uppercase tracking-[0.2em] font-bold text-sm transition-all duration-300 hover:scale-105 shadow-[0_0_20px_rgba(234,179,8,0.2)]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Behandler...
            </> : <>
              Tilmeld
              <Send className="ml-2 size-4" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function MoralskGenopbygning() {
  return <section id="moralsk-genopbygning" className="dark bg-background text-foreground relative py-24 md:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556341/img/fortress_wall_texture.png')] bg-cover bg-center opacity-20 mix-blend-overlay"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background"></div>
        {}
        <div className="absolute inset-0 opacity-5 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556341/img/noise_texture.png')] pointer-events-none"></div>
      </div>

      <div className="container relative z-10 px-4 mx-auto">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-[var(--color-foreground)] mb-6 uppercase tracking-widest border-b-2 border-[var(--color-primary)] inline-block pb-2">
            Moralsk Genopbygning
          </h2>
          <p className="text-lg md:text-xl text-[var(--color-muted-foreground)] font-serif italic leading-relaxed">
            "Som Christian Bak fører jeg en kompromisløs samfundsdebat funderet i militær disciplin og kristen etik. Dette er fundamentet for en nødvendig karakterdannelse og et nådesløst opgør med hykleri. Gennem disciplin, tro og personligt ansvar genopbygger vi de moralske mure, som tidsånden har ladet forfalde."
          </p>
        </div>

        <div className="relative p-1">
          {}
          <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[var(--color-primary)]"></div>
          <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-[var(--color-primary)]"></div>
          <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-[var(--color-primary)]"></div>
          <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[var(--color-primary)]"></div>

          <div className="bg-[var(--color-card)]/30 backdrop-blur-sm p-8 md:p-12 border border-[var(--color-border)]">
            <FormProvider formId="signup_form_home" sectionName="moralsk-genopbygning" defaultValues={{
            full_name: "",
            email_address: "",
            phone_number: "",
            support_reason: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email_address: {
              required: true,
              pattern: "email"
            },
            phone_number: {
              required: false
            },
            support_reason: {
              required: false
            }
          }} submitText="Tilmeld" successMessage="Tak for din tilmelding. Velkommen til frontlinjen." errorMessage="Der opstod en fejl. Prøv venligst igen.">
              <SignUpForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}